import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Download,
  Flag,
  Megaphone,
  Scale,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

/**
 * Admin sub-role → visible nav items.  Role gating is ALSO enforced
 * server-side; this just avoids showing dead links.
 */
const NAV_BY_ROLE = {
  super_admin: [
    { to: "/control-panel-x9", end: true, label: "Verification queue", icon: Users, testId: "admin-nav-queue", badgeKey: "pending" },
    { to: "/control-panel-x9/flagged", label: "Flagged listings", icon: Flag, testId: "admin-nav-flagged" },
    { to: "/control-panel-x9/users", label: "Users", icon: UserCog, testId: "admin-nav-users" },
    { to: "/control-panel-x9/admins", label: "Admin users", icon: ShieldCheck, testId: "admin-nav-admins" },
    { to: "/control-panel-x9/broadcast", label: "Broadcast", icon: Megaphone, testId: "admin-nav-broadcast" },
    { to: "/control-panel-x9/exports", label: "Exports", icon: Download, testId: "admin-nav-exports" },
    { to: "/control-panel-x9/disputes", label: "Disputes", icon: Scale, testId: "admin-nav-disputes" },
    { to: "/control-panel-x9/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings" },
  ],
  verifier: [
    { to: "/control-panel-x9", end: true, label: "Verification queue", icon: Users, testId: "admin-nav-queue", badgeKey: "pending" },
    { to: "/control-panel-x9/flagged", label: "Flagged listings", icon: Flag, testId: "admin-nav-flagged" },
    { to: "/control-panel-x9/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings" },
  ],
  support: [
    { to: "/control-panel-x9", end: true, label: "Users", icon: UserCog, testId: "admin-nav-users" },
    { to: "/control-panel-x9/settings", label: "Settings", icon: Settings, testId: "admin-nav-settings" },
  ],
};

function normalizeRole(r) {
  return r === "admin" ? "super_admin" : r;
}

export default function AdminLayout() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const items = NAV_BY_ROLE[role] || [];
  const [pendingCount, setPendingCount] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // First-login gate: any admin flagged `must_change_password` is hard-
  // pinned to /control-panel-x9/settings until they set a new password.
  // Applied at the LAYOUT so every admin sub-route is covered — not
  // just the index router.
  useEffect(() => {
    if (
      user?.must_change_password &&
      location.pathname !== "/control-panel-x9/settings"
    ) {
      navigate("/control-panel-x9/settings", { replace: true });
    }
  }, [user?.must_change_password, location.pathname, navigate]);

  const loadCount = useCallback(async () => {
    if (role !== "super_admin" && role !== "verifier") return;
    try {
      const { data } = await api.get("/admin/pending-brokers");
      setPendingCount(Array.isArray(data) ? data.length : 0);
    } catch {
      setPendingCount(null);
    }
  }, [role]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  useEffect(() => {
    const handler = () => loadCount();
    window.addEventListener("admin:metrics-refresh", handler);
    return () => window.removeEventListener("admin:metrics-refresh", handler);
  }, [loadCount]);

  return (
    <div className="mb-page flex min-h-screen flex-col" data-testid="admin-layout">
      <AppHeader variant="admin" />
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-2 overflow-x-auto px-6 py-3 md:px-10">
          <ShieldCheck className="h-4 w-4 flex-none text-gray-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Admin
          </span>
          <span
            className="ml-1 inline-flex items-center rounded-full bg-brand-navy/8 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-navy"
            data-testid="admin-role-chip"
          >
            {(role || "").replace("_", " ")}
          </span>
          <div className="mx-2 h-4 w-px flex-none bg-gray-200" />
          <nav className="flex items-center gap-1" data-testid="admin-subnav">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    "mb-transition inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold",
                    isActive
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  )
                }
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
                {item.badgeKey === "pending" && pendingCount !== null && pendingCount > 0 && (
                  <span
                    className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-brand-green px-1.5 py-0.5 text-[10px] font-bold text-white tabular-nums"
                    data-testid="admin-nav-queue-badge"
                  >
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
